import React, { useState } from "react";
import inputStyles from "../../../../assets/styles/components/input.module.css";
import styles from "./SummaryCards.module.css";

interface SummaryCardsProps {
  saldoInicial: number;
  entradas: number;
  saidas: number;
  resultado: number;
  atrasado: number;
  saldoFinal: number;
  saldoConsolidado: number;
  contas: Array<{ uid_conta_financeira: string; nome: string; saldo: number }>;
  dataFilter: any;
  setDataFilter: any;
}

const formatCurrency = (value: number) => {
  return value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

const getFormatDate = (d: Date) => d.toISOString().split("T")[0];

const calculatePeriod = (periodo: string) => {
  const curr = new Date();
  const first = curr.getDate() - curr.getDay();

  const getOffsetDate = (offset: number) => {
    const d = new Date();
    d.setDate(first + offset);
    return getFormatDate(d);
  };

  switch (periodo) {
    case "Essa semana": return { inicio: getOffsetDate(0), fim: getOffsetDate(6) };
    case "Semana que vem": return { inicio: getOffsetDate(7), fim: getOffsetDate(13) };
    case "Semana passada": return { inicio: getOffsetDate(-7), fim: getOffsetDate(-1) };
    case "Esse Mês": return { inicio: getFormatDate(new Date(curr.getFullYear(), curr.getMonth(), 1)), fim: getFormatDate(new Date(curr.getFullYear(), curr.getMonth() + 1, 0)) };
    case "Mês que vem": return { inicio: getFormatDate(new Date(curr.getFullYear(), curr.getMonth() + 1, 1)), fim: getFormatDate(new Date(curr.getFullYear(), curr.getMonth() + 2, 0)) };
    case "Mês passado": return { inicio: getFormatDate(new Date(curr.getFullYear(), curr.getMonth() - 1, 1)), fim: getFormatDate(new Date(curr.getFullYear(), curr.getMonth(), 0)) };
    case "Esse ano": return { inicio: getFormatDate(new Date(curr.getFullYear(), 0, 1)), fim: getFormatDate(new Date(curr.getFullYear(), 11, 31)) };
    case "Ano que vem": return { inicio: getFormatDate(new Date(curr.getFullYear() + 1, 0, 1)), fim: getFormatDate(new Date(curr.getFullYear() + 1, 11, 31)) };
    case "Ano passado": return { inicio: getFormatDate(new Date(curr.getFullYear() - 1, 0, 1)), fim: getFormatDate(new Date(curr.getFullYear() - 1, 11, 31)) };
    default: return null;
  }
};

const SummaryCards: React.FC<SummaryCardsProps> = ({
  saldoInicial, entradas, saidas, resultado, atrasado, saldoFinal, saldoConsolidado, contas, dataFilter, setDataFilter,
}) => {
  const [periodo, setPeriodo] = useState("Esse Mês");

  const handlePeriodChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    setPeriodo(val);
    const dates = calculatePeriod(val);
    if (dates) {
      setDataFilter((prev: any) => ({ ...prev, inicio: dates.inicio, fim: dates.fim }));
    }
  };

  const handleDateChange = (field: string, value: string) => {
    setPeriodo(""); 
    setDataFilter((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleCheck = (field: string, value: boolean) => {
    setDataFilter((prev: any) => ({ ...prev, [field]: value }));
  };

  // --- NOVA LÓGICA DE CLIQUE NOS CARDS ---
  const handleCardClick = (cardType: string) => {
    setDataFilter((prev: any) => {
      const newFilter = { ...prev };

      if (cardType === "entrada") {
        // Se já for entrada, limpa o filtro. Senão, define como entrada.
        newFilter.tipo = prev.tipo === "entrada" ? "" : "entrada";
      } 
      else if (cardType === "saida") {
        // Se já for saida, limpa o filtro. Senão, define como saida.
        newFilter.tipo = prev.tipo === "saida" ? "" : "saida";
      } 
      else if (cardType === "atrasado") {
        // Se já estiver filtrando apenas atrasados, reseta para o padrão (Realizado + Previsto)
        if (prev.incluir_atrasados && !prev.realizado && !prev.previsto) {
          newFilter.incluir_atrasados = false;
          newFilter.realizado = true;
          newFilter.previsto = true;
        } else {
          // Filtra ESTRITAMENTE o que está atrasado
          newFilter.incluir_atrasados = true;
          newFilter.realizado = false;
          newFilter.previsto = false;
          newFilter.tipo = ""; // Limpa tipo de entrada/saída para focar nos atrasados
        }
      }

      return newFilter;
    });
  };

  // Verifica qual filtro está ativo para estilizar o card
  const isOnlyAtrasados = dataFilter.incluir_atrasados && !dataFilter.realizado && !dataFilter.previsto;

  return (
    <div className={styles.container}>
      <div className={styles.topRow}>
        {/* CARDS SUPERIORES */}
        <div className={styles.cardsWrapper}>
          
          {/* 1. Saldo Inicial */}
          <div className={`${styles.card} ${styles.cardBlueOutline}`}>
            <span>Saldo Inicial</span><strong>{formatCurrency(saldoInicial)}</strong>
          </div>

          {/* 2. Entradas */}
          <div 
            className={`${styles.card} ${styles.cardGreen} ${styles.clickable} ${dataFilter.tipo === 'entrada' ? styles.activeEntrada : ''}`}
            onClick={() => handleCardClick("entrada")}
            title="Filtrar apenas Entradas"
          >
            <span>Entradas</span><strong>{formatCurrency(entradas)}</strong>
          </div>

          {/* 3. Saídas */}
          <div 
            className={`${styles.card} ${styles.cardRed} ${styles.clickable} ${dataFilter.tipo === 'saida' ? styles.activeSaida : ''}`}
            onClick={() => handleCardClick("saida")}
            title="Filtrar apenas Saídas"
          >
            <span>Saídas</span><strong>{formatCurrency(saidas)}</strong>
          </div>

          {/* 4. Resultado */}
          <div className={`${styles.card} ${resultado >= 0 ? styles.cardGreen : styles.cardRed}`}>
            <span>Resultado</span><strong>{formatCurrency(resultado)}</strong>
          </div>

          {/* 5. Atrasado */}
          <div 
            className={`${styles.card} ${styles.cardOrange} ${styles.clickable} ${isOnlyAtrasados ? styles.activeAtrasado : ''}`}
            onClick={() => handleCardClick("atrasado")}
            title="Filtrar apenas Atrasados"
          >
            <span>Atrasado</span><strong>{formatCurrency(atrasado)}</strong>
          </div>

          {/* 6. Saldo Final */}
          <div className={`${styles.card} ${styles.cardBlueOutline}`}>
            <span>Saldo Final</span><strong>{formatCurrency(saldoFinal)}</strong>
          </div>
        </div>

        {/* FILTROS RÁPIDOS */}
        <div className={styles.quickFilters}>
          <div className={styles.dateSection}>
            <div className={styles.periodContainer}>
              <select className={inputStyles.input} value={periodo} onChange={handlePeriodChange}>
                <option value="" disabled hidden>Selecione um período</option>
                <option value="Essa semana">Essa semana</option>
                <option value="Semana que vem">Semana que vem</option>
                <option value="Semana passada">Semana passada</option>
                <option value="Esse Mês">Esse Mês</option>
                <option value="Mês que vem">Mês que vem</option>
                <option value="Mês passado">Mês passado</option>
                <option value="Esse ano">Esse ano</option>
                <option value="Ano que vem">Ano que vem</option>
                <option value="Ano passado">Ano passado</option>
              </select>
            </div>
            <div className={styles.dateInputs}>
              <input type="date" className={inputStyles.input} value={dataFilter.inicio || ""} onChange={(e) => handleDateChange("inicio", e.target.value)} />
              <input type="date" className={inputStyles.input} value={dataFilter.fim || ""} onChange={(e) => handleDateChange("fim", e.target.value)} />
            </div>
          </div>
          
          <div className={styles.checkboxes}>
            <label><input type="checkbox" checked={!!dataFilter.realizado} onChange={(e) => handleCheck("realizado", e.target.checked)} /> Realizado</label>
            <label><input type="checkbox" checked={!!dataFilter.previsto} onChange={(e) => handleCheck("previsto", e.target.checked)} /> Previsto</label>
            <label><input type="checkbox" checked={!!dataFilter.incluir_atrasados} onChange={(e) => handleCheck("incluir_atrasados", e.target.checked)} /> Atrasado</label>
          </div>
        </div>
      </div>

      {/* CONTAS BANCÁRIAS E SALDO CONSOLIDADO */}
      <div className={styles.bottomRow}>
        <div className={`${styles.card} ${styles.cardBlueOutline}`} title="Soma de todas as contas">
          <span>Saldo Consolidado</span><strong>{formatCurrency(saldoConsolidado)}</strong>
        </div>

        {contas?.map((conta) => (
          <div key={conta.uid_conta_financeira} className={styles.accountCard}>
            <span>{conta.nome}</span><strong>{formatCurrency(conta.saldo)}</strong>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SummaryCards;